import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * AnnouncementBar — sticky storefront banner.
 *
 * Fetches /api/announcements once on mount.
 * Renders nothing if disabled, no bulletins, or fetch fails.
 *
 * Two display modes (configured in admin):
 *   • marquee — single continuous scroll right→left
 *   • rotate  — cycles through bulletins one at a time, ~4s each
 */
export default function AnnouncementBar() {
  const [cfg, setCfg] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let mounted = true;
    api
      .get("/announcements")
      .then((r) => {
        if (mounted) setCfg(r.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const bulletins = cfg?.bulletins || [];
  const enabled = cfg?.enabled && bulletins.length > 0;

  // Rotate carousel
  useEffect(() => {
    if (!enabled || cfg.mode !== "rotate" || bulletins.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % bulletins.length), 4500);
    return () => clearInterval(t);
  }, [enabled, cfg, bulletins.length]);

  if (!enabled) return null;

  const mode = cfg.mode || "marquee";

  return (
    <div
      className="bg-ink text-cream text-xs sm:text-[13px] tracking-wide relative overflow-hidden"
      data-testid="announcement-bar"
      role="region"
      aria-label="Site announcements"
    >
      {mode === "marquee" ? (
        <MarqueeContent bulletins={bulletins} />
      ) : (
        <RotateContent bulletin={bulletins[idx % bulletins.length]} idx={idx} />
      )}
    </div>
  );
}

function MarqueeContent({ bulletins }) {
  // Repeat the joined text twice for a seamless loop
  const joined = bulletins.map((b) => b.text).join("   ·   ");
  // Speed scales with content length so long content still scrolls visibly
  const seconds = Math.max(20, Math.min(80, joined.length * 0.35));
  return (
    <div className="py-2 announcement-marquee" data-testid="announcement-marquee">
      <div className="announcement-track" style={{ animationDuration: `${seconds}s` }}>
        <span className="announcement-chunk">
          {bulletins.map((b, i) => (
            <BulletinSpan key={`a-${b.id || i}`} b={b} />
          ))}
        </span>
        <span className="announcement-chunk" aria-hidden="true">
          {bulletins.map((b, i) => (
            <BulletinSpan key={`b-${b.id || i}`} b={b} />
          ))}
        </span>
      </div>
    </div>
  );
}

function RotateContent({ bulletin, idx }) {
  if (!bulletin) return null;
  return (
    <div className="py-2 text-center announcement-rotate" data-testid="announcement-rotate">
      <span
        key={`rotate-${idx}`}
        className="inline-block announcement-rotate-item"
      >
        {bulletin.link ? (
          <Link to={bulletin.link} className="hover:underline underline-offset-4">
            {bulletin.text}
          </Link>
        ) : (
          <span>{bulletin.text}</span>
        )}
      </span>
    </div>
  );
}

function BulletinSpan({ b }) {
  return (
    <>
      {b.link ? (
        <Link to={b.link} className="hover:underline underline-offset-4">
          {b.text}
        </Link>
      ) : (
        <span>{b.text}</span>
      )}
      <span className="px-5 text-cream/60" aria-hidden="true">
        ·
      </span>
    </>
  );
}
